/**
 * Team Spain v0 — Capa de Almacenamiento Local Duradero IndexedDB (M5)
 *
 * Implementa una base de datos local desacoplada para soporte offline en campo:
 * - Versionado explícito de esquema con preservación de pendientes en migraciones.
 * - Transacciones atómicas de guardado local (snap + outbox + actualización de captura).
 * - Aislamiento estricto por usuario y equipo.
 * - Detección de fallos de cuota o indisponibilidad de almacenamiento.
 */

import {
  PreparedPackage,
  LocalSnap,
  OutboxOperation,
  DraftState,
  OutboxOperationStatus,
  SyncErrorCategory,
} from '../../types/offline'
import { TeamBlockCapture } from '../../types/fieldCapture'

export const DB_NAME = 'team_spain_offline_v0'
export const DB_VERSION = 1

export const STORES = {
  PREPARED_PACKAGES: 'prepared_packages',
  LOCAL_CAPTURES: 'local_captures',
  LOCAL_SNAPS: 'local_snaps',
  OUTBOX_OPERATIONS: 'outbox_operations',
  DRAFT_STATE: 'draft_state',
} as const

class OfflineIndexedDB {
  private dbPromise: Promise<IDBDatabase> | null = null

  /**
   * Abre o reutiliza la conexión a IndexedDB con versionado controlado
   */
  async getDb(): Promise<IDBDatabase> {
    if (this.dbPromise) {
      return this.dbPromise
    }

    if (typeof indexedDB === 'undefined') {
      throw new Error(
        'IndexedDB no está disponible en este entorno. La persistencia offline no se puede garantizar.'
      )
    }

    this.dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onupgradeneeded = (event) => {
        const db = request.result
        const oldVersion = event.oldVersion

        // Versión 1: Creación de almacenes de objetos e índices
        if (oldVersion < 1) {
          // 1. Paquetes preparados para uso sin conexión
          if (!db.objectStoreNames.contains(STORES.PREPARED_PACKAGES)) {
            const prepStore = db.createObjectStore(STORES.PREPARED_PACKAGES, {
              keyPath: 'capture_id',
            })
            prepStore.createIndex('team_id', 'team_id', { unique: false })
            prepStore.createIndex('user_id', 'user_id', { unique: false })
          }

          // 2. Metadatos de capturas locales
          if (!db.objectStoreNames.contains(STORES.LOCAL_CAPTURES)) {
            const capStore = db.createObjectStore(STORES.LOCAL_CAPTURES, {
              keyPath: 'id',
            })
            capStore.createIndex('team_id', 'team_id', { unique: false })
          }

          // 3. Snaps guardados localmente
          if (!db.objectStoreNames.contains(STORES.LOCAL_SNAPS)) {
            const snapStore = db.createObjectStore(STORES.LOCAL_SNAPS, {
              keyPath: 'id',
            })
            snapStore.createIndex('capture_id', 'capture_id', { unique: false })
            snapStore.createIndex('sync_status', 'sync_status', { unique: false })
            snapStore.createIndex('capture_seq', ['capture_id', 'sequence'], { unique: true })
          }

          // 4. Cola de operaciones Outbox para sincronización idempotente
          if (!db.objectStoreNames.contains(STORES.OUTBOX_OPERATIONS)) {
            const outboxStore = db.createObjectStore(STORES.OUTBOX_OPERATIONS, {
              keyPath: 'operation_id',
            })
            outboxStore.createIndex('capture_id', 'capture_id', { unique: false })
            outboxStore.createIndex('team_id', 'team_id', { unique: false })
            outboxStore.createIndex('user_id', 'user_id', { unique: false })
            outboxStore.createIndex('status', 'status', { unique: false })
            outboxStore.createIndex('sequence_order', 'sequence_order', { unique: false })
          }

          // 5. Estado de borrador en progreso (recuperable tras recarga o cierre)
          if (!db.objectStoreNames.contains(STORES.DRAFT_STATE)) {
            db.createObjectStore(STORES.DRAFT_STATE, {
              keyPath: 'capture_id',
            })
          }
        }
      }

      request.onsuccess = () => {
        const db = request.result

        db.onversionchange = () => {
          db.close()
          this.dbPromise = null
        }

        resolve(db)
      }

      request.onerror = () => {
        this.dbPromise = null
        reject(new Error(`Error al abrir IndexedDB: ${request.error?.message || 'Error desconocido'}`))
      }

      request.onblocked = () => {
        console.warn('La apertura de IndexedDB está bloqueada por otra pestaña abierta.')
      }
    })

    return this.dbPromise
  }

  /**
   * Cierra la conexión activa a IndexedDB (útil para pruebas y reinicio de sesión)
   */
  async close(): Promise<void> {
    if (this.dbPromise) {
      try {
        const db = await this.dbPromise
        db.close()
      } catch {
        // ignorar
      }
      this.dbPromise = null
    }
  }

  // ==========================================================================
  // PAQUETES PREPARADOS ("Preparar para usar sin conexión")
  // ==========================================================================

  async savePreparedPackage(pkg: PreparedPackage): Promise<void> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORES.PREPARED_PACKAGES, STORES.LOCAL_CAPTURES], 'readwrite')
      const prepStore = tx.objectStore(STORES.PREPARED_PACKAGES)
      const capStore = tx.objectStore(STORES.LOCAL_CAPTURES)

      prepStore.put(pkg)
      capStore.put({
        ...pkg.capture,
        last_modified_locally: new Date().toISOString(),
      })

      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(new Error(`Error al guardar paquete offline: ${tx.error?.message}`))
      tx.onabort = () => reject(new Error('Transacción de guardado offline abortada'))
    })
  }

  async getPreparedPackage(captureId: string): Promise<PreparedPackage | null> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.PREPARED_PACKAGES, 'readonly')
      const store = tx.objectStore(STORES.PREPARED_PACKAGES)
      const req = store.get(captureId)

      req.onsuccess = () => resolve(req.result || null)
      req.onerror = () => reject(new Error(`Error al leer paquete preparado: ${req.error?.message}`))
    })
  }

  async getAllPreparedPackages(teamId: string, userId: string): Promise<PreparedPackage[]> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.PREPARED_PACKAGES, 'readonly')
      const store = tx.objectStore(STORES.PREPARED_PACKAGES)
      const req = store.getAll()

      req.onsuccess = () => {
        const all: PreparedPackage[] = req.result || []
        // Filtrar por equipo y usuario autorizado
        resolve(all.filter((p) => p.team_id === teamId && p.user_id === userId))
      }
      req.onerror = () => reject(new Error(`Error al listar paquetes preparados: ${req.error?.message}`))
    })
  }

  // ==========================================================================
  // BORRADOR DEL SNAP EN PROGRESO (Draft State)
  // ==========================================================================

  async saveDraftState(draft: DraftState): Promise<void> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.DRAFT_STATE, 'readwrite')
      const store = tx.objectStore(STORES.DRAFT_STATE)
      store.put(draft)

      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(new Error(`Error al guardar borrador local: ${tx.error?.message}`))
    })
  }

  async getDraftState(captureId: string): Promise<DraftState | null> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.DRAFT_STATE, 'readonly')
      const store = tx.objectStore(STORES.DRAFT_STATE)
      const req = store.get(captureId)

      req.onsuccess = () => resolve(req.result || null)
      req.onerror = () => reject(new Error(`Error al leer borrador local: ${req.error?.message}`))
    })
  }

  async clearDraftState(captureId: string): Promise<void> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.DRAFT_STATE, 'readwrite')
      const store = tx.objectStore(STORES.DRAFT_STATE)
      store.delete(captureId)

      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(new Error(`Error al limpiar borrador local: ${tx.error?.message}`))
    })
  }

  // ==========================================================================
  // CAPTURAS LOCALES
  // ==========================================================================

  async saveLocalCapture(capture: TeamBlockCapture): Promise<void> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.LOCAL_CAPTURES, 'readwrite')
      const store = tx.objectStore(STORES.LOCAL_CAPTURES)
      store.put({
        ...capture,
        last_modified_locally: new Date().toISOString(),
      })

      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(new Error(`Error al guardar captura local: ${tx.error?.message}`))
    })
  }

  async getLocalCapture(captureId: string): Promise<TeamBlockCapture | null> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.LOCAL_CAPTURES, 'readonly')
      const store = tx.objectStore(STORES.LOCAL_CAPTURES)
      const req = store.get(captureId)

      req.onsuccess = () => resolve(req.result || null)
      req.onerror = () => reject(new Error(`Error al leer captura local: ${req.error?.message}`))
    })
  }

  // ==========================================================================
  // GUARDADO ATÓMICO: SNAP LOCAL + OUTBOX + LIMPIEZA DE BORRADOR
  // ==========================================================================

  async saveLocalSnapAtomic(params: {
    snap: LocalSnap
    outboxOp: OutboxOperation
    updatedCapture?: Partial<TeamBlockCapture> & { id: string }
  }): Promise<void> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(
        [
          STORES.LOCAL_SNAPS,
          STORES.OUTBOX_OPERATIONS,
          STORES.LOCAL_CAPTURES,
          STORES.DRAFT_STATE,
        ],
        'readwrite'
      )

      // 1. Guardar el snap en local_snaps
      const snapStore = tx.objectStore(STORES.LOCAL_SNAPS)
      snapStore.put(params.snap)

      // 2. Encolar la operación en outbox_operations
      const outboxStore = tx.objectStore(STORES.OUTBOX_OPERATIONS)
      outboxStore.put(params.outboxOp)

      // 3. Actualizar metadatos de captura si se proporcionan
      if (params.updatedCapture) {
        const capStore = tx.objectStore(STORES.LOCAL_CAPTURES)
        const getReq = capStore.get(params.updatedCapture.id)
        getReq.onsuccess = () => {
          const existing = getReq.result || {}
          capStore.put({
            ...existing,
            ...params.updatedCapture,
            last_modified_locally: new Date().toISOString(),
          })
        }
      }

      // 4. Limpiar el borrador de ese snap
      const draftStore = tx.objectStore(STORES.DRAFT_STATE)
      draftStore.delete(params.snap.capture_id)

      tx.oncomplete = () => resolve()
      tx.onerror = () =>
        reject(
          new Error(
            `Error en transacción atómica de guardado local: ${tx.error?.message || 'Error desconocido'}`
          )
        )
      tx.onabort = () =>
        reject(new Error('Transacción atómica de guardado local abortada.'))
    })
  }

  /**
   * Encola una operación en la outbox sin snap nuevo (ej. corrección, finalización, marcador)
   */
  async enqueueOutboxOperation(operation: OutboxOperation): Promise<void> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.OUTBOX_OPERATIONS, 'readwrite')
      const store = tx.objectStore(STORES.OUTBOX_OPERATIONS)
      store.put(operation)

      tx.oncomplete = () => resolve()
      tx.onerror = () =>
        reject(new Error(`Error al encolar operación en outbox: ${tx.error?.message}`))
    })
  }

  // ==========================================================================
  // CONSULTA DE SNAPS LOCALES Y COLA DE OUTBOX
  // ==========================================================================

  async getLocalSnaps(captureId: string): Promise<LocalSnap[]> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.LOCAL_SNAPS, 'readonly')
      const store = tx.objectStore(STORES.LOCAL_SNAPS)
      const index = store.index('capture_id')
      const req = index.getAll(captureId)

      req.onsuccess = () => {
        const snaps: LocalSnap[] = req.result || []
        // Ordenar siempre por sequence ascendente
        snaps.sort((a, b) => a.sequence - b.sequence)
        resolve(snaps)
      }
      req.onerror = () => reject(new Error(`Error al leer snaps locales: ${req.error?.message}`))
    })
  }

  async markSnapSynced(snapId: string, serverRevision?: number): Promise<void> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.LOCAL_SNAPS, 'readwrite')
      const store = tx.objectStore(STORES.LOCAL_SNAPS)
      const getReq = store.get(snapId)

      getReq.onsuccess = () => {
        const snap = getReq.result as LocalSnap | undefined
        if (snap) {
          snap.sync_status = 'synced'
          if (serverRevision !== undefined) {
            snap.revision = serverRevision
          }
          store.put(snap)
        }
      }

      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(new Error(`Error al marcar snap sincronizado: ${tx.error?.message}`))
    })
  }

  async getPendingOutboxOperations(
    teamId?: string,
    userId?: string
  ): Promise<OutboxOperation[]> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.OUTBOX_OPERATIONS, 'readonly')
      const store = tx.objectStore(STORES.OUTBOX_OPERATIONS)
      const req = store.getAll()

      req.onsuccess = () => {
        let ops: OutboxOperation[] = req.result || []

        // Filtrar por estados no finalizados
        ops = ops.filter(
          (o) => o.status === 'pending' || o.status === 'syncing' || o.status === 'conflict'
        )

        // Filtrar por equipo y usuario si se aportan
        if (teamId) {
          ops = ops.filter((o) => o.team_id === teamId)
        }
        if (userId) {
          ops = ops.filter((o) => o.user_id === userId)
        }

        // Ordenar estrictamente por sequence_order ascendente
        ops.sort((a, b) => a.sequence_order - b.sequence_order)
        resolve(ops)
      }

      req.onerror = () => reject(new Error(`Error al leer outbox: ${req.error?.message}`))
    })
  }

  async updateOutboxOperationStatus(
    operationId: string,
    status: OutboxOperationStatus,
    errorMsg?: string,
    errorCategory?: SyncErrorCategory
  ): Promise<void> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.OUTBOX_OPERATIONS, 'readwrite')
      const store = tx.objectStore(STORES.OUTBOX_OPERATIONS)
      const req = store.get(operationId)

      req.onsuccess = () => {
        const op = req.result as OutboxOperation | undefined
        if (op) {
          op.status = status
          op.last_attempt_at = new Date().toISOString()
          if (status === 'syncing') {
            op.attempts = (op.attempts || 0) + 1
          }
          if (errorMsg !== undefined) {
            op.last_error = errorMsg
          }
          if (errorCategory !== undefined) {
            op.error_category = errorCategory
          }
          store.put(op)
        }
      }

      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(new Error(`Error al actualizar estado outbox: ${tx.error?.message}`))
    })
  }

  async removeOutboxOperation(operationId: string): Promise<void> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.OUTBOX_OPERATIONS, 'readwrite')
      const store = tx.objectStore(STORES.OUTBOX_OPERATIONS)
      store.delete(operationId)

      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(new Error(`Error al eliminar operación outbox: ${tx.error?.message}`))
    })
  }

  async countPendingOperations(
    captureId?: string,
    teamId?: string,
    userId?: string
  ): Promise<number> {
    const ops = await this.getPendingOutboxOperations(teamId, userId)
    if (captureId) {
      return ops.filter(
        (o) =>
          o.payload?.capture_id === captureId ||
          o.payload?.snap?.capture_id === captureId ||
          o.entity_id === captureId ||
          (o as any).capture_id === captureId
      ).length
    }
    return ops.length
  }

  // ==========================================================================
  // EXPORTACIÓN DE EMERGENCIA Y PRIVACIDAD
  // ==========================================================================

  /**
   * Genera un volcado JSON deportivo de emergencia de las operaciones pendientes
   * estrictamente acotado al usuario y equipo, sin secretos ni notas clínicas.
   */
  async exportEmergencyBackup(teamId: string, userId: string): Promise<string> {
    const ops = await this.getPendingOutboxOperations(teamId, userId)
    const db = await this.getDb()

    const snaps: LocalSnap[] = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.LOCAL_SNAPS, 'readonly')
      const store = tx.objectStore(STORES.LOCAL_SNAPS)
      const req = store.getAll()
      req.onsuccess = () => {
        const all: LocalSnap[] = req.result || []
        resolve(all.filter((s) => s.team_id === teamId))
      }
      req.onerror = () => reject(req.error)
    })

    const backup = {
      export_version: '1.0',
      exported_at: new Date().toISOString(),
      environment:
        (typeof import.meta !== 'undefined' && (import.meta as any).env?.MODE) ||
        (typeof globalThis !== 'undefined' && (globalThis as any).process?.env?.NODE_ENV) ||
        'production',
      team_id: teamId,
      user_id: userId,
      pending_operations_count: ops.length,
      pending_operations: ops,
      outbox_operations: ops,
      local_snaps: snaps,
    }

    return JSON.stringify(backup, null, 2)
  }

  /**
   * Limpia el almacenamiento de un usuario específico de forma segura
   */
  async clearUserData(userId: string): Promise<void> {
    const db = await this.getDb()
    return new Promise((resolve, reject) => {
      const tx = db.transaction(
        [STORES.PREPARED_PACKAGES, STORES.DRAFT_STATE, STORES.OUTBOX_OPERATIONS],
        'readwrite'
      )

      // Borrar borradores del usuario
      const draftStore = tx.objectStore(STORES.DRAFT_STATE)
      const dReq = draftStore.getAll()
      dReq.onsuccess = () => {
        for (const item of (dReq.result || []) as DraftState[]) {
          if (item.user_id === userId) {
            draftStore.delete(item.capture_id)
          }
        }
      }

      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(new Error(`Error al limpiar datos del usuario: ${tx.error?.message}`))
    })
  }
}

export const offlineIndexedDb = new OfflineIndexedDB()
